import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Mail,
  Phone,
  Calendar,
  Shield,
  Key,
  Clock,
  User,
  UserCheck,
  UserX,
  AlertCircle,
  CheckCircle,
  Settings
} from 'lucide-react';
import AppShell from '../../components/AppShell';
import Guard, { ProtectedButton } from '../../components/Guard';
import useAdminStore from '../../store';

const UserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const {
    getUserById,
    getUserGroups,
    getUserEffectivePermissions,
    groups,
    permissions,
    deleteUser,
    updateUser
  } = useAdminStore();

  const [user, setUser] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [effectivePermissions, setEffectivePermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const loadUserData = () => {
      const userData = getUserById(userId);
      if (userData) {
        setUser(userData);
        setUserGroups(getUserGroups(userId));
        setEffectivePermissions(getUserEffectivePermissions(userId));
      }
      setLoading(false);
    };

    loadUserData();
  }, [userId, getUserById, getUserGroups, getUserEffectivePermissions]);

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete ${user.displayName}?`)) {
      try {
        await deleteUser(user.id);
        navigate('/admin-iam/users');
      } catch (error) {
        alert(`Failed to delete user: ${error.message}`);
      }
    }
  };

  const handleToggleStatus = async () => {
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    try {
      await updateUser(user.id, { status: newStatus });
      setUser({ ...user, status: newStatus });
    } catch (error) {
      alert(`Failed to update user status: ${error.message}`);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'inactive':
        return <UserX className="w-5 h-5 text-gray-500" />;
      case 'suspended':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <User className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      doctor: 'bg-blue-100 text-blue-800',
      nurse: 'bg-green-100 text-green-800',
      receptionist: 'bg-purple-100 text-purple-800',
      manager: 'bg-orange-100 text-orange-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: User },
    { id: 'groups', name: 'Groups', icon: UserCheck },
    { id: 'permissions', name: 'Permissions', icon: Key },
    { id: 'activity', name: 'Activity', icon: Clock }
  ];

  if (loading) {
    return (
      <AppShell title="User Details">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-32 bg-gray-200 rounded mb-6"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </AppShell>
    );
  }

  if (!user) {
    return (
      <AppShell title="User Not Found">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">User Not Found</h2>
            <p className="text-gray-600 mb-6">The user you're looking for doesn't exist.</p>
            <button
              onClick={() => navigate('/admin-iam/users')}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Users
            </button>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title={`${user.displayName} - User Details`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Guard permission="users.read">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/admin-iam/users')}
              className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Users
            </button>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                  <span className="text-xl font-bold text-blue-600">
                    {user.displayName.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{user.displayName}</h1>
                  <p className="text-gray-600">{user.email}</p>
                  <div className="flex items-center mt-2 space-x-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user.status)}`}>
                      {getStatusIcon(user.status)}
                      <span className="ml-1 capitalize">{user.status}</span>
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                      <Shield className="w-3 h-3 mr-1" />
                      {user.role}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex space-x-2">
                <ProtectedButton
                  permission="users.write"
                  onClick={handleToggleStatus}
                  className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg border ${
                    user.status === 'active' 
                      ? 'border-red-300 text-red-700 bg-white hover:bg-red-50' 
                      : 'border-green-300 text-green-700 bg-white hover:bg-green-50'
                  }`}
                >
                  {user.status === 'active' ? (
                    <>
                      <UserX className="w-4 h-4 mr-2" />
                      Deactivate
                    </>
                  ) : (
                    <>
                      <UserCheck className="w-4 h-4 mr-2" />
                      Activate
                    </>
                  )}
                </ProtectedButton>

                <ProtectedButton
                  permission="users.write"
                  onClick={() => navigate(`/admin-iam/users/${user.id}/edit`)}
                  className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </ProtectedButton>

                <ProtectedButton
                  permission="users.delete"
                  onClick={handleDelete}
                  className="inline-flex items-center px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </ProtectedButton>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4 inline mr-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Basic Information
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <User className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Full Name</p>
                        <p className="text-sm text-gray-600">{user.displayName}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Mail className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Email</p>
                        <p className="text-sm text-gray-600">{user.email}</p>
                      </div>
                    </div>
                    {user.phone && (
                      <div className="flex items-center">
                        <Phone className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Phone</p>
                          <p className="text-sm text-gray-600">{user.phone}</p>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center">
                      <Shield className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Role</p>
                        <p className="text-sm text-gray-600 capitalize">{user.role}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Calendar className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Created</p>
                        <p className="text-sm text-gray-600">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    {user.lastLogin && (
                      <div className="flex items-center">
                        <Clock className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Last Login</p>
                          <p className="text-sm text-gray-600">
                            {new Date(user.lastLogin).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Account Status */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Account Status
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">Status</span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user.status)}`}>
                        {getStatusIcon(user.status)}
                        <span className="ml-1 capitalize">{user.status}</span>
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">Two-Factor Auth</span>
                      <span className="text-sm text-gray-600">
                        {user.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">Groups</span>
                      <span className="text-sm text-gray-600">
                        {userGroups.length} group{userGroups.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">Permissions</span>
                      <span className="text-sm text-gray-600">
                        {effectivePermissions.length} permission{effectivePermissions.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'groups' && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">User Groups</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Groups that this user belongs to
                  </p>
                </div>
                <div className="p-6">
                  {userGroups.length === 0 ? (
                    <div className="text-center py-8">
                      <UserCheck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">This user is not a member of any groups</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {userGroups.map((group) => (
                        <div key={group.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                              <UserCheck className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-gray-900">{group.name}</h4>
                              <p className="text-sm text-gray-500">{group.description}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-500">
                              {group.permissions?.length || 0} permissions
                            </p>
                            <p className="text-xs text-gray-500">
                              {group.members?.length || 0} members
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'permissions' && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Effective Permissions</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    All permissions granted to this user through groups and direct assignment
                  </p>
                </div>
                <div className="p-6">
                  {effectivePermissions.length === 0 ? (
                    <div className="text-center py-8">
                      <Key className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">This user has no permissions</p>
                    </div>
                  ) : effectivePermissions.includes('*') ? (
                    <div className="text-center py-8">
                      <Shield className="w-12 h-12 text-red-500 mx-auto mb-4" />
                      <h4 className="text-lg font-medium text-gray-900 mb-2">Administrator</h4>
                      <p className="text-gray-500">This user has full administrative access</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {effectivePermissions.map((permKey) => {
                        const permission = permissions.find(p => p.key === permKey);
                        return (
                          <div key={permKey} className="flex items-center p-3 border border-gray-200 rounded-lg">
                            <Key className="w-5 h-5 text-blue-500 mr-3" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {permission?.name || permKey}
                              </p>
                              <p className="text-xs text-gray-500">
                                {permission?.description || 'No description available'}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Recent actions performed by this user
                  </p>
                </div>
                <div className="p-6">
                  <div className="text-center py-8">
                    <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Activity tracking is not yet implemented</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Guard>
      </div>
    </AppShell>
  );
};

export default UserDetailPage;
